import {
  Injectable,
  Renderer2,
  RendererFactory2,
  Inject,
  signal,
  WritableSignal,
  DOCUMENT
} from '@angular/core';

import { ThemeMode } from '@shared/enums/theme-mode';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private renderer: Renderer2;
  // Señal para el tema seleccionado por el usuario: 'light', 'dark', o 'system'
  public theme: WritableSignal<ThemeMode> = signal(
    (localStorage.getItem('theme') as ThemeMode) || ThemeMode.SYSTEM
  );

  private readonly DARK_CLASS = 'dark';
  private readonly LIGHT_CLASS = 'light';

  constructor(
    rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.initializeTheme();
  }

  private getSystemPreference(): 'light' | 'dark' {
    return this.document.defaultView?.matchMedia('(prefers-color-scheme: dark)')
      .matches
      ? 'dark'
      : 'light';
  }

  private applyTheme(mode: ThemeMode) {
    // 1. Elimina clases de temas anteriores
    this.renderer.removeClass(this.document.body, this.DARK_CLASS);
    this.renderer.removeClass(this.document.body, this.LIGHT_CLASS);
    // 2. Aplica la lógica del tema y almacena la preferencia
    switch (mode) {
      case ThemeMode.DARK:
        this.renderer.addClass(this.document.body, this.DARK_CLASS);
        localStorage.setItem('theme', 'dark');
        // Establecer 'color-scheme' manualmente en 'dark' para sobrescribir el sistema.
        this.renderer.setStyle(this.document.body, 'color-scheme', 'dark');
        break;
      case ThemeMode.LIGHT:
        this.renderer.addClass(this.document.body, this.LIGHT_CLASS);
        localStorage.setItem('theme', 'light');
        // Establecer 'color-scheme' manualmente en 'light'
        this.renderer.setStyle(this.document.body, 'color-scheme', 'light');
        break;
      default:
        // 'system'
        // Remueve y agrega la clase dark o light dependiendo de las preferencias del usuario
        // esto para que tanto como tailwind y material lo apliquen mediante la clase
        // .dark o .light
        localStorage.setItem('theme', 'system');
        const systemMode = this.getSystemPreference();
        if (systemMode == ThemeMode.DARK) {
          this.renderer.removeClass(this.document.body, this.LIGHT_CLASS);
          this.renderer.addClass(this.document.body, this.DARK_CLASS);
        } else if (systemMode == ThemeMode.LIGHT) {
          this.renderer.removeClass(this.document.body, this.DARK_CLASS);
          this.renderer.addClass(this.document.body, this.LIGHT_CLASS);
        }
        break;
    }
  }

  // Lógica de inicialización y escucha de cambios en la preferencia del sistema
  private initializeTheme() {
    this.applyTheme(this.theme());
    // Escuchar cambios en la preferencia del sistema SOLO si el tema actual es 'system'
    const mediaQuery = this.document.defaultView?.matchMedia(
      '(prefers-color-scheme: dark)'
    );
    if (mediaQuery) {
      mediaQuery.addEventListener('change', () => {
        if (this.theme() === 'system') {
          // Reaplicar el tema 'system' para que el CSS se refresque si cambia
          this.applyTheme(ThemeMode.SYSTEM);
        }
      });
    }
  }

  setTheme(mode: ThemeMode) {
    this.theme.set(mode);
    this.applyTheme(mode);
  }
}
